// @owners { team: patients }
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { setDeliveries } from '~shared/actions/deliveries';
import { type CancelationReason } from '~shared/constants';
import getIsAuthorized from '~shared/features/auth/selectors/getIsAuthorized';
import { type DeliveryWithPrescription } from '~shared/features/delivery-info/types';
import { put } from '~shared/helpers/apiHelper';
import { getErrorMessageFromResponse } from '~shared/helpers/helper';
import { staleTimes } from '~shared/queries/constants';
import {
  deliveries as deliveriesKeys,
  invalidateDeliveries,
  removeDeliveriesFromCache,
} from '~shared/queries/query-keys/deliveries';
import { invalidateShipments } from '~shared/queries/query-keys/shipments';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type LightDelivery } from '~shared/types';

const CANCEL_DELIVERIES_API_VERSION = 'v5';

/**
 * Fetch the patient's deliveries — the React Query replacement for the deprecated fetchDeliveries
 * thunk (Option D). Runs two independent queries:
 * - `all`: the expensive full list, long staleTime (refetched rarely)
 * - `active`: the cheap in-flight subset, short staleTime (refreshed often)
 * and merges them at read time (active overrides all by id), mirroring the old fetch-all + upsert-active.
 */
export const useGetDeliveries = () => {
  const isAuthorized = useSelectorShared(getIsAuthorized);

  const allQuery = useQuery({
    ...deliveriesKeys.all,
    enabled: isAuthorized,
    staleTime: staleTimes.fifteenMinutes,
    gcTime: staleTimes.fifteenMinutes,
  });
  const activeQuery = useQuery({
    ...deliveriesKeys.active,
    enabled: isAuthorized,
    staleTime: staleTimes.oneMinute,
  });

  const deliveries = useMemo(() => {
    const byID = new Map<number, LightDelivery>();
    for (const delivery of allQuery.data ?? []) {
      byID.set(delivery.id, delivery);
    }
    // active is fresher — let it override the cached full-list copy
    for (const delivery of activeQuery.data ?? []) {
      byID.set(delivery.id, delivery);
    }
    return [...byID.values()];
  }, [allQuery.data, activeQuery.data]);

  return {
    deliveries,
    // the full list is the primary source; active is an enhancement on top
    isLoading: allQuery.isLoading,
    isSuccess: allQuery.isSuccess,
  };
};

/**
 * Bridge: mirror the merged React Query deliveries into the Redux `deliveries` slice so existing
 * Reselect selectors (getDeliveries → checkout's getOrder, etc.) keep working without a Redux
 * networking action. One-directional (RQ → Redux). Mount once at an always-rendered authed root.
 * Transitional bridge toward strategy A — see the redux-thunk-to-react-query-migration skill.
 */
export const useDeliveriesReduxBridge = () => {
  const dispatch = useDispatchShared();
  const { deliveries, isSuccess } = useGetDeliveries();

  useEffect(() => {
    if (isSuccess) {
      dispatch(setDeliveries(deliveries));
    }
  }, [dispatch, isSuccess, deliveries]);
};

type CancelDeliveriesParams = {
  deliveries: DeliveryWithPrescription[];
  cancelationReason: CancelationReason;
  cancelationInfo: string;
  turnOffAutoRefill: boolean;
};

/**
 * Cancel deliveries — React Query replacement for the deprecated cancelDeliveries thunk.
 * Optimistically removes the canceled deliveries from the query caches (so the UI updates without a
 * full refetch, like the old reducer), then invalidates deliveries + shipments so they re-sync.
 */
export const useCancelDeliveries = () =>
  useMutation({
    mutationFn: async ({
      deliveries,
      cancelationReason,
      cancelationInfo,
      turnOffAutoRefill,
    }: CancelDeliveriesParams) => {
      const response = await put(
        '/deliveries/cancel',
        {
          deliveries,
          cancelation_reason: cancelationReason,
          cancelation_info: cancelationInfo,
          turn_off_auto_refill: turnOffAutoRefill,
        },
        { version: CANCEL_DELIVERIES_API_VERSION },
      );
      if (response.error) {
        throw new Error(getErrorMessageFromResponse(response));
      }
      return deliveries;
    },
    onSuccess: (canceledDeliveries) => {
      removeDeliveriesFromCache(canceledDeliveries.map((delivery) => delivery.id));
      void invalidateDeliveries();
      void invalidateShipments();
    },
  });
